import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row, Skeleton } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import getDiscountCodeInfo, { DiscountInfo } from 'core/application/account/discountCodeInfo'
import SubscriptionFrequency from 'core/application/adminSignUp/common/subscriptionFrequency'
import MakePaymentValues from 'core/application/adminSignUp/makePayment'
import { ProductIds } from 'core/application/commons/config'
import getSubscriptionFees, { SubscriptionFeesResponse } from 'core/application/subscriptions/getSubscriptionFees'
import getSubscriptionPlansForPackage, {
  SubscriptionPlansForPackageResponse,
} from 'core/application/subscriptions/getSubscriptionPlansForPackage'

import CheckBox from 'common/components/dataEntry/formik/FormikCheckboxField'
import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Button from 'common/components/general/Button'
import StepItemPageHeader from 'common/components/onBoarding/StepItemPageHeader'
import SubscriptionTypes from 'common/components/subscriptionTypes'

import { MAX_QSEHRA_EMPLOYEES } from 'common/constants'
import { BaseNavigationProps } from 'common/wizard/BaseNavigationProps'

import pdf from 'assets/docs/PeopleKeepServicesAgreementWorkPerks.pdf'

import useCommandRequest from '../../../common/hooks/useCommandRequest'
import useCurrentProduct from '../../../common/hooks/useCurrentProduct'
import useQueryRequest from '../../../common/hooks/useQueryRequest'
import { formatMoney } from '../../../common/moneyFormatter'
import WizardNextButton from '../../common/WizardNextButton'

import './styles.less'

interface SubscriptionFeesProps extends BaseNavigationProps<MakePaymentValues> {
  name: string
  serviceAgreement: boolean
  discountCode: string | null
  planCode: string | null
  subscriptionPackageCode?: string
  goNext: (from: string) => void
  goBack: (from: string) => void
  hasDirtyData: (dirtyData: boolean) => void
  submitFormHandler: (request: any) => Promise<any>
}
const SubscriptionFees: React.FC<SubscriptionFeesProps> = (props) => {
  const { t: translate } = useTranslation()
  const product = useCurrentProduct()
  const [sendCommand, isLoading] = useCommandRequest()
  const monthNumber = 12
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState<SubscriptionPlansForPackageResponse>(null)

  const handleSubscriptionPlanChange = (found: SubscriptionPlansForPackageResponse) =>
    setSelectedSubscriptionPlan(found)

  const onSubmit = async (values: any) => {
    const currentSeatNumber =
      values.seatNumber < selectedSubscriptionPlan.minimumSeats
        ? selectedSubscriptionPlan.minimumSeats
        : values.seatNumber

    const request: any = {
      planCode: selectedSubscriptionPlan.code,
      seatNumber: currentSeatNumber,
    }

    const result = await sendCommand(() => props.submitFormHandler(request))

    if (result.completed) {
      props.goNext(props.name)
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={{
        productId: product.productId,
        serviceAgreement: false,
        seatNumber: props.state.seatNumber || selectedSubscriptionPlan?.minimumSeats,
      }}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
      }}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <Row>
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 3 }}>
              <div className="flex flex-col" style={{ maxWidth: 800 }}>
                <Row>
                  <Col span={18}>
                    <StepItemPageHeader
                      className={'ml-0'}
                      title={translate('admin-signup.subscription.title')}
                      numberOfSteps={2}
                      currentStep={1}
                      step={translate('admin-signup.subscription.subscription-fees.subscription-label')}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xs={24} md={18}>
                    <label className="subscription-types">
                      {translate('admin-signup.subscription.subscription-fees.subscription-type-label')}
                    </label>
                  </Col>
                </Row>

                <SubscriptionTypes
                  selectedSubscriptionPlan={selectedSubscriptionPlan}
                  serviceAgreement={props.serviceAgreement}
                  onSubscriptionPlanChange={handleSubscriptionPlanChange}
                  subscriptionPackageCode={props.subscriptionPackageCode}
                  discountCode={props.discountCode}
                />
              </div>
            </Col>
          </Row>

          <Row className="bottom__actions">
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 3 }}>
              <Button
                icon="fa fa-arrow-left"
                size="large"
                htmlType="button"
                type="default"
                onClick={() => {
                  props.goBack(props.name)
                }}
              >
                {translate('general.back')}
              </Button>
              <WizardNextButton disabled={!formValidator(translate).isValidSync(formik.values)} loading={isLoading} />
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  )
}

export default SubscriptionFees

const formValidator = (translate: any) =>
  Yup.object({
    productId: Yup.number(),
    serviceAgreement: Yup.bool().oneOf([true], translate('general.required')),
    seatNumber: Yup.number().when('productId', {
      is: ProductIds.QsehraId,
      then: Yup.number().max(MAX_QSEHRA_EMPLOYEES),
    }),
  })
